import { Button } from 'components/atoms';
import InputMessage from 'components/atoms/inputs/wrapper/message/InputMessage';
import { FormikErrors } from 'formik';
import { useTranslation } from 'react-i18next';
import { PiLock, PiWarning, PiCaretLeft, PiCaretRight } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { II18nextPrefix } from 'types/components/components.interface';
import './TabsNavigation.scss';
import { useRef, useEffect, useState } from 'react';
import { useTokenData } from 'store/state';

interface ITabsNavigation {
 name: string;
 value: number;
 error?:
  | string
  | (string | undefined)[]
  | FormikErrors<any>[]
  | FormikErrors<any>;
 helper?: string;
 permissionName?: string;
}

interface ITabsNavigationProps extends II18nextPrefix {
 tabs: ITabsNavigation[];
 disabled?: number[];
 displayMode?: boolean;
}

const TabsNavigation = ({
 i18nextPrefix,
 disabled,
 tabs,
 displayMode,
}: ITabsNavigationProps) => {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const { pathname } = useLocation();
 const tokenData = useTokenData();

 const isTabActive = (name: string): boolean =>
  pathname.split('/').includes(name);

 const isTabDisabled = (value: number): boolean => !!disabled?.includes(value);

 const hasPermission = (permissionName?: string): boolean => {
  if (!permissionName) return true;
  return !!tokenData?.[permissionName as keyof typeof tokenData];
 };

 const elementRef = useRef<HTMLDivElement | null>(null);

 const [isOverflowing, setIsOverflowing] = useState(false);

 const checkOverflow = () => {
  if (elementRef.current) {
   const isHorOverflowing =
    elementRef.current.scrollWidth > elementRef.current.clientWidth;
   setIsOverflowing(isHorOverflowing);
  }
 };

 useEffect(() => {
  checkOverflow();
  window.addEventListener('resize', checkOverflow);
  return () => {
   window.removeEventListener('resize', checkOverflow);
  };
 }, []);

 const handleScroll = (direction: 'left' | 'right') => {
  if (elementRef.current) {
   const scrollAmount = elementRef.current.clientWidth * 0.8;
   if (direction === 'right') {
    elementRef.current.scrollLeft += scrollAmount;
   } else {
    elementRef.current.scrollLeft -= scrollAmount;
   }
  }
 };

 return (
  <div className='tab-container'>
   <div
    onClick={() => handleScroll('left')}
    className='bg-white  rounded-bl-md flex justify-center items-center hover:opacity-85 hover:cursor-pointer'
    style={{
     height: '44px',
     display: isOverflowing ? 'flex' : 'none',
     width: '44px',
    }}
   >
    <Button.Icon icon={<PiCaretLeft />} />
   </div>

   <div
    ref={elementRef}
    className='tabs-navigation'
    style={{
     overflowX: isOverflowing ? 'auto' : undefined,
     overflowY: !isOverflowing ? undefined : 'hidden'
    }}
   >
    {tabs.map((tab, idx) => {
     if (!hasPermission(tab.permissionName)) return null;
     
     return (
      <Button
       wrapperClassNames={!!tab.error ? 'is-message' : undefined}
       className={!displayMode && !!tab.error ? 'error' : undefined}
       style={{
        background: idx === 0 && isOverflowing ? 'white' : 'none',
       }}
       disabled={isTabDisabled(tab.value)}
       key={tab.name}
       isActive={isTabActive(tab.name)}
       onClick={() =>
        navigate(tab.name, {
         relative: 'path',
         replace: true,
        })
       }
       variant={BUTTON_VARIANT.white}
      >
       {!tab.error && isTabDisabled(tab.value) && (
        <Button.Icon icon={<PiLock />} />
       )}
       {!displayMode && !!tab.error && <Button.Icon icon={<PiWarning />} />}
       <Button.Text text={t(`${i18nextPrefix}.tabs.${tab.name}`)} />
       {((typeof tab.error === 'string' && tab.error.length) ||
        !!tab.helper?.length) && (
        <InputMessage
         helperText={tab.helper}
         message={typeof tab.error === 'string' ? tab.error : undefined}
        />
       )}
      </Button>
     );
    })}
    <div
     className='extender'
     style={{
      display: !isOverflowing ? 'block' : 'none',
     }}
    />
   </div>

   <div
    onClick={() => handleScroll('right')}
    className='bg-white  rounded-br-md flex justify-center items-center hover:opacity-85 hover:cursor-pointer'
    style={{
     height: '44px',
     display: isOverflowing ? 'flex' : 'none',
     width: '44px',
    }}
   >
    <Button.Icon icon={<PiCaretRight />} />
   </div>
  </div>
 );
};

export default TabsNavigation;
